import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { toNumber } from '../ui/format'
import { Banner, Button, ErrorBanner, Field, NumberInput, Panel, Spinner } from '@shop/ui'

type SalesPolicy = { minMarginPercent: number }

/**
 * إعداداتُ البيع — <b>ما يقرّره صاحبُ المحلّ ولا يُكتب في الشيفرة</b>.
 *
 * <p>
 * ⚠ <b>وهذه الشاشةُ شرطُ ألّا يصير الإعدادُ طريقًا مسدودًا.</b> رقمٌ في
 * <c>platform.settings</c> بلا مكانٍ يُغيَّر منه يُكتب مرّةً في هجرةٍ ثمّ
 * يُنسى — ويُطلب تغييرُه يومًا فيكون الجوابُ «نشرةٌ جديدة».
 * </p>
 */
export default function SalesSettings() {
  const { can } = useAuth()
  const queries = useQueryClient()
  const [draft, setDraft] = useState<string | null>(null)

  const policy = useQuery({
    queryKey: ['settings', 'sales'],
    queryFn: () => api<SalesPolicy>('/api/v1/settings/sales'),
  })

  const save = useMutation({
    mutationFn: (percent: number) =>
      api<SalesPolicy>('/api/v1/settings/sales', {
        method: 'PUT',
        body: { minMarginPercent: percent },
      }),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ['settings', 'sales'] })
      setDraft(null)
    },
  })

  const current = policy.data?.minMarginPercent ?? 0
  const shown = draft ?? String(current)
  const parsed = toNumber(shown)
  const valid = parsed !== null && parsed >= 0 && parsed <= 900
  const dirty = parsed !== null && parsed !== current

  return (
    <Panel title="إعداداتُ البيع">
      {policy.isPending && <Spinner />}
      {policy.isError && <ErrorBanner error={policy.error} />}

      {policy.data && (
        <>
          <Field
            label="أدنى هامشٍ فوق الكلفة (%)"
            hint="صفر = لا تبِع بخسارة. و١٠ = لا تبِع بأقلَّ من الكلفة زائدَ عُشرها."
          >
            <NumberInput
              value={shown}
              onChange={setDraft}
              placeholder="0"
              disabled={!can(Perm.settingEdit) || save.isPending}
            />
          </Field>

          <div className="mt-3">
            <Banner tone="info">
              الكاشيرُ يخصم في هذا المدى ولا ينزل تحته.
              <b> ومن يملك «البيعَ بأقلَّ من الكلفة» يتجاوزه</b> — وتُمنح من
              شاشة الصلاحيّات.
            </Banner>
          </div>

          {/*
            ⚠ **وصنفٌ بلا كلفةٍ في الدفتر لا أرضيّةَ له** — والصمتُ عن ذلك
              يجعل صاحبَ المحلّ يظنّ أنّ الحمايةَ شاملة.
          */}
          <p className="mt-2 text-xs text-ink-soft">
            ⚠ وصنفٌ أُدخل بلا كلفة لا يُقاس عليه حدٌّ — يظهر في تقرير الربح
            ضمن «أسطرٌ بلا كلفة».
          </p>

          {save.isError && (
            <div className="mt-3">
              <ErrorBanner error={save.error} />
            </div>
          )}

          {can(Perm.settingEdit) && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                tone="primary"
                disabled={!valid || !dirty || save.isPending}
                onClick={() => save.mutate(parsed!)}
              >
                {save.isPending ? 'يُحفظ…' : 'حفظ'}
              </Button>

              {dirty && <Button onClick={() => setDraft(null)}>تراجع</Button>}
            </div>
          )}
        </>
      )}
    </Panel>
  )
}
